import React, { useEffect, useState } from "react";
import { useAuth } from "../../AuthContext";
import api from "../../api";
import EnrollmentRecord from "./EnrollmentRecord";
import { messageOf } from "./SignatureApi";
import "./signatures.css";
const statusLabels = {
  DRAFT: "Em preenchimento",
  PENDING: "Em análise",
  APPROVED: "Aprovada",
  REJECTED: "Rejeitada",
  CORRECTION: "Correção solicitada",
  REVOKED: "Desabilitada",
  EXPIRED: "Expirada",
};
const checks = {
  MATCH: "Confere",
  MISMATCH: "Divergente",
  NOT_REGISTERED: "Não exigido",
  UNREADABLE: "Não lido",
  PROFILE_MISSING: "Cadastro incompleto",
  READING_CONFLICT: "Leitura inconsistente",
};
const date = (value) =>
  value ? String(value).slice(0, 10).split("-").reverse().join("/") : "—";
export default function UserSignatureIdentification({ userId }) {
  const { user } = useAuth();
  const [view, setView] = useState({
    key: "",
    record: null,
    busy: true,
    error: "",
  });
  const [revision, setRevision] = useState(0);
  const key = `${user?.id}:${userId}`;
  useEffect(() => {
    if (user?.isMaster !== true || !userId) return;
    let alive = true;
    setView({ key, record: null, busy: true, error: "" });
    api
      .get(`/signatures/enrollment/users/${userId}`)
      .then(({ data }) => {
        if (data && Number(data.userId) !== Number(userId))
          throw new Error(
            "A identificação recebida não corresponde ao usuário selecionado.",
          );
        if (alive) setView({ key, record: data, busy: false, error: "" });
      })
      .catch((e) => {
        if (alive)
          setView({ key, record: null, busy: false, error: messageOf(e) });
      });
    return () => {
      alive = false;
    };
  }, [user?.isMaster, userId, key, revision]);
  const { record, busy, error } =
    view.key === key ? view : { record: null, busy: true, error: "" };
  if (user?.isMaster !== true || !userId) return null;
  return (
    <section
      className="signature-panel"
      aria-label="Identificação do usuário para assinaturas"
    >
      <div className="signature-toolbar">
        <strong>
          {record
            ? statusLabels[record.status] || record.status
            : "Identificação"}
        </strong>
        <button
          type="button"
          disabled={busy}
          onClick={() => setRevision((v) => v + 1)}
        >
          Atualizar identificação
        </button>
      </div>
      {error && (
        <p role="alert" className="signature-error">
          {error}
        </p>
      )}
      {busy ? (
        <p role="status">Consultando identificação…</p>
      ) : !error && !record ? (
        <p>Nenhuma identificação registrada.</p>
      ) : (
        !error &&
        record && (
          <>
            <dl>
              <dt>Nome do registro</dt>
              <dd>{record.name}</dd>
              <dt>CPF</dt>
              <dd>{record.cpf || "—"}</dd>
              <dt>Nascimento</dt>
              <dd>{date(record.birthDate)}</dd>
            </dl>
            {record.profileCurrent === false && (
              <p className="signature-help">
                Os dados da conta foram alterados após este registro.
              </p>
            )}
            {(record.documentPortrait || record.supplementPortrait) && (
              <details className="signature-card">
                <summary>Presença de foto no documento</summary>
                <p>
                  Primeiro arquivo:{" "}
                  {record.documentPortrait?.reason || "Não conferido"}
                </p>
                {record.supplementPortrait && (
                  <p>Complementar: {record.supplementPortrait.reason}</p>
                )}
                <p className="signature-help">
                  Verifica somente presença da foto. Não há comparação facial ou
                  porcentagem de identidade.
                </p>
              </details>
            )}
            {record.analysis && (
              <details className="signature-card">
                <summary>Conferência da Nash IA</summary>
                <div className="signature-analysis-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Campo</th>
                        <th>Cadastro do registro</th>
                        <th>Documento</th>
                        <th>Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Nome", "name"],
                        ["CPF", "cpf"],
                        ["Nascimento", "birthDate"],
                      ].map(([label, key]) => (
                        <tr key={key}>
                          <td>{label}</td>
                          <td>
                            {key === "birthDate"
                              ? date(record[key])
                              : record[key] || "—"}
                          </td>
                          <td>
                            {key === "birthDate"
                              ? date(record.analysis.extracted?.[key])
                              : record.analysis.extracted?.[key] || "Não lido"}
                          </td>
                          <td>
                            {checks[record.analysis.checks?.[key]] ||
                              "Pendente"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="signature-help">{record.analysis.reason}</p>
              </details>
            )}
            {record.imagesAvailable ? (
              <EnrollmentRecord record={record} administrative />
            ) : (
              <p className="signature-help">
                {record.status === "DRAFT"
                  ? "Identificação ainda não enviada."
                  : "Imagens indisponíveis ou fora do prazo de retenção."}
              </p>
            )}
            {record.selfieQuality && (
              <p className="signature-help">
                Qualidade da foto: {record.selfieQuality.reason || "Em análise"}
                . Essa análise não é reconhecimento facial.
              </p>
            )}
            {record.acceptedAt && (
              <p className="signature-help">
                Aceite registrado em{" "}
                {new Date(record.acceptedAt).toLocaleString("pt-BR")}.
              </p>
            )}
          </>
        )
      )}
    </section>
  );
}
